import { ReactNode, useLayoutEffect, useMemo } from 'react';
import { ReportVerdict } from '@/reports/types';
import { SheetContext, SheetRegistry } from './Sheet';
import { useOutlineStore } from './Outline';

/**
 * The printed page. Everything inside takes the report design language, and
 * the sheet numbers its sections, figures and tables in print order (Sheet.tsx).
 */
export function ReportSheet({ children, footer }: { children: ReactNode; footer?: ReactNode }) {
  const registry = useMemo(() => new SheetRegistry(), []);
  // a fresh count on every render: numbers follow what prints this time
  registry.reset();
  const outline = useOutlineStore();
  outline?.beginPass();
  useLayoutEffect(() => {
    outline?.commit(registry.sections);
  });
  return (
    <SheetContext.Provider value={registry}>
      <div className="veriform-report">
        <div className="sheet">
          <main>{children}</main>
          {footer && <footer>{footer}</footer>}
        </div>
      </div>
    </SheetContext.Provider>
  );
}

export function verdictClass(verdict: ReportVerdict): 'passed' | 'failed' | 'warned' {
  return verdict === 'passed' ? 'passed' : verdict === 'failed' ? 'failed' : 'warned';
}

export interface HeaderMeta {
  label: string;
  value: ReactNode;
}

interface ReportHeaderProps {
  brand?: string;
  kind: string;
  verdict: ReportVerdict;
  verdictLabel: string;
  status?: string;
  title: ReactNode;
  titleSub?: ReactNode;
  subtitle?: ReactNode;
  meta?: HeaderMeta[];
  children?: ReactNode;
}

/** Cover block: brand line, verdict pill, title, meta chips. The verdict also colours the rule beneath. */
export function ReportHeader({ brand = 'Fusaware', kind, verdict, verdictLabel, status, title, titleSub, subtitle, meta = [], children }: ReportHeaderProps) {
  return (
    <header className={`report-header ${verdictClass(verdict)}`}>
      <div className="header-inner">
        <div className="brand"><b>{brand}</b><span>{kind}</span></div>
        <div className="verdict-row">
          <span className={`verdict-pill ${verdictClass(verdict)}`}>{verdictLabel}</span>
          {status && <span className="report-status">{status}</span>}
        </div>
        <h1>{title}</h1>
        {titleSub && <p className="title-sub">{titleSub}</p>}
        {subtitle && <p className="subtitle">{subtitle}</p>}
        {meta.length > 0 && (
          <div className="header-meta">
            {meta.map((m) => (
              <span key={m.label}><span className="hm-label">{m.label}</span>{m.value}</span>
            ))}
          </div>
        )}
        {children}
      </div>
    </header>
  );
}

interface ReportSectionProps {
  id: string;
  number: string;
  title: ReactNode;
  children: ReactNode;
}

/** A section with a number the report gives it; `Section` (Sheet.tsx) numbers itself in print order. */
export function ReportSection({ id, number, title, children }: ReportSectionProps) {
  return (
    <section className="card" aria-labelledby={`${id}-heading`}>
      <h2 id={id}><span id={`${id}-heading`}>{number} · {title}</span></h2>
      <div className="card-body">{children}</div>
    </section>
  );
}

export function ReportSubsection({ id, number, title, children }: ReportSectionProps) {
  return (
    <>
      <h3 id={id}>{number} · {title}</h3>
      {children}
    </>
  );
}
